// v1.27.6 — SILENT boot recovery. Users must never see a scary "we hit a
// snag" card that undermines their trust in the app. Instead:
//   1. First React render error → auto-wipe SW/caches/storage and reload
//      exactly ONCE. Users just see a very brief neutral overlay ("Refreshing
//      the app…") — no red flags, no technical language.
//   2. If the reload STILL fails (rare — genuine code bug not fixed by cache
//      wipe), we still auto-reload silently up to 3 times spaced 4s apart.
//   3. Only if all attempts fail do we show a minimal single-line friendly
//      message with a Reload button. This is a last-resort UI the user
//      should essentially never see.
//
// The 4-per-minute reload cap from v1.27.5 is preserved to guarantee no
// infinite loop.

import React from "react";

const RELOAD_COUNTER_KEY = "micm-boot-reload-count";
const RELOAD_WINDOW_MS = 60_000;
const SILENT_RECOVERY_KEY = "micm-silent-recovery-attempts";
const MAX_SILENT_RECOVERIES = 3;

function bumpReloadCounter() {
    try {
        const now = Date.now();
        const raw = sessionStorage.getItem(RELOAD_COUNTER_KEY);
        let parsed = { count: 0, first: now };
        if (raw) { try { parsed = JSON.parse(raw); } catch (_e) {} }
        if (now - (parsed.first || 0) > RELOAD_WINDOW_MS) parsed = { count: 0, first: now };
        parsed.count = (parsed.count || 0) + 1;
        sessionStorage.setItem(RELOAD_COUNTER_KEY, JSON.stringify(parsed));
        return parsed.count;
    } catch (_e) { return 0; }
}

export function markBootSuccess() {
    try {
        sessionStorage.removeItem(RELOAD_COUNTER_KEY);
        sessionStorage.removeItem(SILENT_RECOVERY_KEY);
    } catch (_e) {}
}

// Any hard-reload path in the app (autoUpdate.js) goes through this helper.
// Enforces a 4-per-minute cap so a rogue SW state can't loop forever.
export function reloadIfSafe(reason) {
    const c = bumpReloadCounter();
    if (c > 4) {
        console.warn(`[MICM] reload suppressed after ${c} attempts (${reason})`);
        return false;
    }
    const sep = window.location.search ? "&" : "?";
    window.location.replace(window.location.pathname + window.location.search + sep + "_swv=" + Date.now());
    return true;
}

async function silentWipeAndReload() {
    // Count how many silent recoveries we've already attempted this session.
    let attempts = 0;
    try { attempts = parseInt(sessionStorage.getItem(SILENT_RECOVERY_KEY) || "0", 10) || 0; } catch (_e) {}
    try { sessionStorage.setItem(SILENT_RECOVERY_KEY, String(attempts + 1)); } catch (_e) {}
    try {
        if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
        }
        if (window.caches) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
        }
        // Only wipe local/session storage on the FIRST attempt — some app
        // state (auth token) is expensive to re-collect. On subsequent
        // attempts we still preserve it.
        if (attempts === 0) {
            try {
                // Preserve the auth token so the user isn't logged out during
                // an invisible cache refresh.
                const tokenKeys = Object.keys(localStorage).filter((k) => /token|auth/i.test(k));
                const preserved = {};
                tokenKeys.forEach((k) => { preserved[k] = localStorage.getItem(k); });
                localStorage.clear();
                Object.entries(preserved).forEach(([k, v]) => { if (v != null) localStorage.setItem(k, v); });
            } catch (_e) {}
        }
    } catch (_e) { /* non-fatal */ }
    window.location.replace("/?_r=" + Date.now());
}

export class BootErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, showFallback: false };
    }

    static getDerivedStateFromError(_error) {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        console.error("[MICM] BootErrorBoundary caught:", error, info);
        let attempts = 0;
        try { attempts = parseInt(sessionStorage.getItem(SILENT_RECOVERY_KEY) || "0", 10) || 0; } catch (_e) {}
        if (attempts < MAX_SILENT_RECOVERIES) {
            // Silent recovery: show a neutral overlay for ~500ms then reload.
            // The user perceives a normal loading spinner, nothing scary.
            setTimeout(() => { silentWipeAndReload(); }, 500);
        } else {
            // All silent attempts exhausted — show minimal friendly UI.
            this.setState({ showFallback: true });
        }
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        // Recovery in progress — show a neutral loading state that looks like
        // a normal app boot spinner. NO error language, NO stack traces.
        if (!this.state.showFallback) {
            return (
                <div style={{
                    minHeight: "100vh", background: "#ffffff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
                }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                        <div style={{
                            width: 44, height: 44, borderRadius: "50%",
                            border: "3px solid #e2e8f0", borderTopColor: "#4f46e5",
                            animation: "micm-spin 0.7s linear infinite",
                        }} />
                        <div style={{ fontSize: 13, color: "#64748b" }}>Refreshing the app…</div>
                        <style>{"@keyframes micm-spin{to{transform:rotate(360deg)}}"}</style>
                    </div>
                </div>
            );
        }

        // Last resort — silent recovery failed multiple times. Minimal
        // friendly UI, no stack trace, no scary language.
        return (
            <div style={{
                minHeight: "100vh", background: "#ffffff",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "24px",
                fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
            }}>
                <div style={{ maxWidth: 360, textAlign: "center" }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🔄</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}>Please reload the app</div>
                    <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.55, marginBottom: 24 }}>
                        We're just tidying up in the background. Tap below to continue.
                    </div>
                    <button
                        data-testid="soft-reload-btn"
                        onClick={silentWipeAndReload}
                        style={{
                            width: "100%", padding: "14px 18px", borderRadius: 12,
                            border: 0, background: "#4f46e5", color: "white",
                            fontWeight: 600, fontSize: 15, cursor: "pointer",
                        }}
                    >Reload</button>
                </div>
            </div>
        );
    }
}
